import { type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import { createRepoEnablementPicker } from '../lib/repo-enablement-picker';
import { canOwnerWrite } from '../lib/roles';
import { useMe } from '../lib/session';
import { count } from '../lib/time';
import { EmptyState } from './EmptyState';
import { LoadMore } from './LoadMore';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { RepoEnablementAct } from './RepoEnablementAct';
import { RepoEnablementConfirm } from './RepoEnablementConfirm';
import { RepoEnablementFilters } from './RepoEnablementFilters';
import { RepoEnablementResult } from './RepoEnablementResult';
import { RepoEnablementSelection } from './RepoEnablementSelection';
import { RepoEnablementTable } from './RepoEnablementTable';
import { Skeleton } from './Skeleton';

// Where and how (#7802): which of the account's repos are maintained, and with what starting
// policy. Nothing is on until a person says so, and this is where they say it at scale.
//
// Paged, not virtualised: the server filters, a page is 50 rows, and `Select all matching` selects
// the FILTER. A person never has to load 5,000 rows to act on them, so no list here is ever that
// long. The number in the confirm is the server's dry run, never a count of rows in the browser,
// and the act refuses (409) if the selection stops matching it.

export function RepoEnablementPanel(props: {
  readonly token: string | null;
  /** The account's repos changed: a container's own reads re-read. */
  readonly onChanged?: () => void;
}): JSX.Element {
  const me = useMe(props.token);
  // `write:repos` mirror: shut while `/v1/me` is in flight, and `denied` is not `!allowed`.
  const allowed = (): boolean => !me.isLoading && canOwnerWrite(me.data?.role);
  const denied = (): boolean => !me.isLoading && !canOwnerWrite(me.data?.role);
  const picker = createRepoEnablementPicker({ token: props.token, onChanged: props.onChanged });
  const matching = (): number => picker.matching() ?? 0;

  return (
    <Panel title={t('repo_enablement.title')}>
      <p class="repo-enablement__line">{t('repo_enablement.lead')}</p>
      <RepoEnablementFilters
        filter={picker.filter()}
        facets={picker.facets.data}
        onChange={picker.applyFilter}
      />
      <Show when={picker.matching() !== null}>
        <p class="repo-enablement__line" aria-live="polite">
          {tPlural('repo_enablement.matching', matching(), { shown: count(matching()) })}
        </p>
      </Show>
      <Show when={denied()}>
        <p class="repo-enablement__line" role="status">
          {t('repo_enablement.denied')}
        </p>
      </Show>
      <Show when={allowed()}>
        <RepoEnablementSelection picker={picker} />
      </Show>
      <QueryBoundary
        query={picker.list.view}
        isEmpty={(rows) => rows.length === 0}
        loading={<Skeleton height="10rem" />}
        empty={
          <EmptyState
            message={
              Object.keys(picker.filter()).length === 0
                ? t('repo_enablement.empty')
                : t('repo_enablement.no_match')
            }
          />
        }
      >
        {(rows) => <RepoEnablementTable picker={picker} canAct={allowed()} rows={rows} />}
      </QueryBoundary>
      <LoadMore list={picker.list} />
      <Show when={allowed()}>
        <RepoEnablementAct picker={picker} />
      </Show>
      <Show when={picker.act.pending()}>
        <p class="repo-enablement__line" role="status">
          {t('repo_enablement.working')}
        </p>
      </Show>
      <Show when={picker.ask.error() ?? picker.act.error()}>
        {(reason) => (
          <p class="repo-enablement__line" role="alert">
            {reason()}
          </p>
        )}
      </Show>
      <Show when={picker.result()}>{(answer) => <RepoEnablementResult answer={answer()} />}</Show>
      <RepoEnablementConfirm picker={picker} />
    </Panel>
  );
}
